<template>
  <global-modal name="event-modal-participant-passes" @before-open="beforeOpen">
    <Modal v-if="event" @close="$modal.hide('event-modal-participant-passes')">
      <EventModalPassesHeader
        :options="header"
        @download="downloadAllPasses"
        @print="printAllPasses"
        :hide-quota="
          Number(getLocaleValue(exhibition, 'create_personal_pass_disabled'))
        "
      >
        <template slot="title">
          {{ $t("event.passes.participantPasses") }}
        </template>

        <template
          slot="deadlines-text"
          v-if="passDeadline.startInstallation && passDeadline.endDismantling"
        >
          {{
            $t("event.passes.participant.deadlinesText", {
              from: startDate,
              to: endDate
            })
          }}
        </template>
        <template slot="pass-warning">
          <span
            v-html="getLocaleValue(exhibition, 'personal_pass_info_1')"
          ></span>
        </template>
        <template
          v-if="getLocaleValue(exhibition, 'personal_pass_info_2').length"
          slot="pass-warning2"
        >
          <span
            v-html="getLocaleValue(exhibition, 'personal_pass_info_2')"
          ></span>
        </template>
        <template slot="quota-text">
          {{ $t("event.passes.participant.quotaText") }}
        </template>

        <template
          v-if="secondsFromDeadlineCertificatePrint >= 0 && header.quota.total"
          slot="warning-message"
        >
          <icon name="warning--no-color" />
          {{
            $t("event.passes.participant.warningMessage", {
              date: passDeadline.startCertificatePrint.format("DD.MM.YYYY")
            })
          }}
        </template>
        <template slot="pass_terms">
          <span
            v-html="getLocaleValue(exhibition, 'personal_pass_info_3')"
          ></span>
        </template>
      </EventModalPassesHeader>
      <div class="loader-wrap" v-if="isLoading">
        <Loader is-absolute white opacity />
      </div>
      <template
        v-else-if="
          !Number(getLocaleValue(exhibition, 'create_personal_pass_disabled'))
        "
      >
        <EventModalPassesAddForm
          v-if="
            hasAccessToWrite &&
              (isAddFormShown || !participantPasses.length) &&
              diffInSeconds(passDeadline.certificate) >= 0 &&
              !isQuotaFull
          "
          :is-submitting="isAddFormSubmitting"
          :show-hide="!!participantPasses.length"
          package-upload
          package-upload-participant
          :package-upload-template-src="
            $t('event.passes.participant.downloadBlank')
          "
          :package-upload-template-size="
            $t('event.passes.participant.packageUploadSize')
          "
          :has-errors="errors.any()"
          @package-upload-handler="uploadPackage"
          @submit="submit"
          @hide="isAddFormShown = false"
          :submit-disabled="isVisaSubmittingDisabled"
          class="event-modal-participant-passes__add"
        >
          <template slot="form-title">
            {{ $t("event.passes.participant.addTitle") }}
          </template>

          <div class="event-modal-passes-add-form__title">
            {{ $t("event.passes.participant.applicationTitle") }}
          </div>

          <div class="event-modal-passes-add-form__row flex">
            <div class="event-modal-passes-add-form__row-col _380">
              <InputLabel>
                <template #label>
                  {{ $t("event.passes.participant.companyName") }}
                </template>

                <input
                  v-model="companyName"
                  v-validate="'required'"
                  type="text"
                  name="companyName"
                />

                <template v-if="errors.has('companyName')" slot="error">
                  {{ errors.first("companyName") }}
                </template>
              </InputLabel>
            </div>
            <div class="event-modal-passes-add-form__row-col _380">
              <InputLabel>
                <template #label>
                  {{ $t("common.lastName") }}
                </template>

                <input
                  v-model="lastName"
                  v-validate="'required|custom_alpha|min:1|max:50'"
                  type="text"
                  name="lastName"
                />

                <template v-if="errors.has('lastName')" slot="error">
                  {{ errors.first("lastName") }}
                </template>
              </InputLabel>
            </div>
          </div>

          <div class="event-modal-passes-add-form__row flex">
            <div class="event-modal-passes-add-form__row-col _380">
              <InputLabel>
                <template #label>
                  {{ $t("common.firstName") }}
                </template>

                <input
                  v-model="firstName"
                  v-validate="'required|custom_alpha|min:1|max:50'"
                  type="text"
                  name="firstName"
                />

                <template v-if="errors.has('firstName')" slot="error">
                  {{ errors.first("firstName") }}
                </template>
              </InputLabel>
            </div>
            <div class="event-modal-passes-add-form__row-col _380">
              <InputLabel>
                <template #label>
                  {{ $t("common.middleName") }}
                </template>

                <input
                  name="middleName"
                  v-model="middleName"
                  type="text"
                  v-validate="'custom_alpha|min:1|max:50'"
                />

                <template v-if="errors.has('middleName')" slot="error">
                  {{ errors.first("middleName") }}
                </template>
              </InputLabel>
            </div>
          </div>
          <div
            class="event-modal-passes-add-form__row flex"
            v-if="isDaysUntilInstallationDeadline"
          >
            <div class="event-modal-passes-add-form__row-col _560">
              <Checkbox v-model="isVisa">
                {{ $t("event.passes.participant.isVisaInvite") }}
              </Checkbox>
            </div>
          </div>

          <template v-if="isVisa">
            <div
              style="padding-bottom: 15px"
              class="event-modal-passes-add-form__row text-description"
            >
              {{ $t("event.passes.participant.eVisaInfo") }}
              <a
                href="https://electronic-visa.kdmid.ru/index_en.html"
                target="_blank"
                class="text-description link-text"
              >
                https://electronic-visa.kdmid.ru/index_en.html
              </a>
            </div>
            <div class="event-modal-passes-add-form__row text-description">
              {{ $t("event.passes.participant.visaInfo") }}
            </div>
            <div class="event-modal-passes-add-form__row flex">
              <div class="event-modal-passes-add-form__row-col _380">
                <input-label>
                  <template #label>
                    {{ $t("event.passes.participant.chooseCitizenship") }}
                  </template>
                  <Select
                    name="country"
                    v-validate="{
                      required: true,
                      single_dropdown_in: countryList
                    }"
                    v-model="citizenship"
                    :options="countryList"
                  ></Select>
                  <template slot="error" v-if="errors.has('country')">
                    {{ errors.first("country") }}
                  </template>
                </input-label>
              </div>
            </div>

            <div class="event-modal-passes-add-form__row flex">
              <div class="event-modal-passes-add-form__row-col _200">
                <InputLabel :error-position-static="true">
                  <template #label>
                    {{ $t("common.birthday") }}
                  </template>

                  <input
                    :disabled="isVisaDisabled"
                    v-model="birthday"
                    v-mask="'##.##.####'"
                    v-validate="{
                      required: true,
                      check_date_format: 'DD.MM.YYYY',
                      single_datepicker_greater_or_equal: minDate,
                      single_datepicker_less_or_equal: currentDate
                    }"
                    data-vv-delay="500"
                    type="text"
                    :placeholder="
                      $t('event.passes.participant.birthdayPlaceholder')
                    "
                    name="birthday"
                  />

                  <template v-if="errors.has('birthday')" slot="error">
                    {{ errors.first("birthday") }}
                  </template>
                </InputLabel>
              </div>

              <div class="event-modal-passes-add-form__row-col _560">
                <InputLabel :error-position-static="true">
                  <template #label>
                    {{ $t("common.birthplace") }}
                  </template>

                  <input
                    :disabled="isVisaDisabled"
                    v-model="birthplace"
                    v-validate="'required|if_digits_then_letters'"
                    type="text"
                    :placeholder="
                      $t('event.passes.participant.birthplacePlaceholder')
                    "
                    name="birthplace"
                  />

                  <template v-if="errors.has('birthplace')" slot="error">
                    {{ errors.first("birthplace") }}
                  </template>
                </InputLabel>
              </div>
            </div>
            <div
              class="event-modal-passes-add-form__row flex text-description"
              v-if="isVisa"
            >
              <div class="event-modal-passes-add-form__row-col _200">
                <div class="manager-text">
                  <div>
                    {{ $t("event.passes.participant.visaHelp1") }}
                  </div>
                  <div>
                    {{ $t("event.passes.participant.visaHelp2") }}
                  </div>
                </div>

                <a class="text-description link-text" href="tel:+74992563023">
                  +7 (499) 428-04-62
                </a>
                <a
                  class="text-description link-text"
                  href="mailto:visa@expocentr.ru"
                >
                  visa@expocentr.ru
                </a>
              </div>
              <div
                v-if="citizenship"
                class="event-modal-passes-add-form__row-col _560"
              >
                <template
                  v-if="!isSimpleVisaTimeExpired && isCountryVisaSimple"
                >
                  <p>{{ $t("event.passes.participant.visaSimpleInfo1") }}</p>
                  <p>{{ $t("event.passes.participant.visaSimpleInfo2") }}</p>
                  <p>{{ $t("event.passes.participant.visaSimpleInfo3") }}</p>
                </template>
                <template
                  v-else-if="isSimpleVisaTimeExpired && isCountryVisaSimple"
                >
                  {{
                    $t("event.passes.participant.visaSimpleLateNotificationL")
                  }}
                </template>

                <template
                  v-if="!isAverageVisaTimeExpired && !isCountryVisaSimple"
                >
                  <p>
                    {{ $t("event.passes.participant.visaNotSimpleInfo1") }}
                  </p>
                  <p>
                    {{ $t("event.passes.participant.visaNotSimpleInfo2") }}
                  </p>
                  <p>
                    {{ $t("event.passes.participant.visaNotSimpleInfo3") }}
                  </p>
                </template>
                <template
                  v-else-if="isAverageVisaTimeExpired && !isCountryVisaSimple"
                >
                  {{
                    $t("event.passes.participant.visaAverageLateNotification")
                  }}
                </template>
              </div>
            </div>
          </template>
        </EventModalPassesAddForm>

        <EventModalPassesUsers
          type="certificate"
          :users="participantPasses"
          :deadline="passDeadline.certificate"
          :deadlineStartPrint="passDeadline.startCertificatePrint"
          :deadlineEndPrint="passDeadline.endCertificatePrint"
          :allow-adding-pass="!isQuotaFull"
          :is-loading="isLoadingWithOpacity"
          :sorting="participantPassSorting"
          @add="addNewPass"
          @download="downloadPass"
          @print="printPass"
          @delete="deletePass"
          @delete-all="deleteAllPasses"
          @upload="sortingPasses"
          @sortPasses="sortingPasses"
          class="event-modal-participant-passes__users"
        />
        <div class="event-modal-participant-passes__users">
          <pagination
            class="event-modal-participant-passes__pagination"
            v-if="participantPassesPaginationLength > 1"
            :page="currentPage"
            :page-count="participantPassesPaginationLength"
            :total-visible="paginationPageAmount"
            @change="paginate"
          />
        </div>
      </template>
    </Modal>
  </global-modal>
</template>

<script>
import Modal from "@/components/Modal/Modal.vue";
import EventModalPassesHeader from "./EventModalPassesHeader.vue";
import EventModalPassesAddForm from "./EventModalPassesAddForm.vue";
import EventModalPassesUsers from "./EventModalPassesUsers.vue";

import moment from "moment";
import { mapState, mapGetters } from "vuex";

import diffInSeconds from "@/helpers/diffInSeconds";

import apiPersonalPass from "@/api/personalPass";
import apiCommon from "@/api/common";

import {
  Loader,
  Pagination,
  Checkbox,
  InputLabel,
  Select
} from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";
import printJS from "print-js";
import getError from "@/helpers/error";

import { getLocaleValue } from "@/helpers/common";

export default {
  name: "EventModalParticipantPasses",

  components: {
    Checkbox,
    Pagination,
    Loader,
    Modal,
    Select,
    InputLabel,

    EventModalPassesHeader,
    EventModalPassesAddForm,
    EventModalPassesUsers
  },

  data: () => ({
    isLoading: false,
    isLoadingWithOpacity: false,
    isAddFormShown: false,
    isAddFormSubmitting: false,
    test: true,
    companyName: "",
    lastName: "",
    firstName: "",
    middleName: "",
    birthday: "",
    birthplace: "",
    isVisa: false,
    citizenship: "",
    countryList: [],
    uploadPackageError: "",
    currentPage: 1
  }),

  computed: {
    ...mapState("event", ["event"]),
    ...mapState("passes", [
      "participantPasses",
      "participantPassesPaginationLength",
      "participantPassSorting",
      "participantPassesAmount"
    ]),
    ...mapGetters("event", [
      "exhibition",
      "hasAccessToWrite",
      "eventId",
      "passDeadline",
      "isEventMontage",
      "subParticipantAviableQuotas"
    ]),

    minDate() {
      return moment("1900-01-01 00:00:00");
    },

    currentDate() {
      return moment();
    },
    paginationPageAmount() {
      return window.innerWidth > 570 ? 5 : 2;
    },
    header() {
      return {
        quota: {
          count: this.participantPassesAmount,
          total:
            this.availablePersonalPassesAmount -
            this.subParticipantAviableQuotas
        },
        deadline: this.passDeadline.certificate,
        deadlineStartPrint: this.passDeadline.startCertificatePrint,
        deadlineEndPrint: this.passDeadline.endCertificatePrint,
        passes: this.participantPasses
      };
    },

    isCompleted() {
      return (
        this.companyName &&
        this.lastName &&
        this.firstName &&
        this.middleName &&
        this.birthday &&
        this.birthplace
      );
    },

    isQuotaFull() {
      return this.header.quota.count >= this.header.quota.total;
    },

    secondsFromDeadlineCertificatePrint() {
      if (!this.passDeadline && !this.passDeadline.startCertificatePrint) {
        return Infinity;
      }

      return diffInSeconds(this.passDeadline.startCertificatePrint);
    },

    availablePersonalPassesAmount() {
      if (!this.event.quota) return 0;

      return this.event.quota.certificate;
    },
    isCountryVisaSimple() {
      return this.countryList.find(item => item?.value === this.citizenship)
        ?.isVisaSimple;
    },
    isSimpleVisaTimeExpired() {
      return moment() >= moment(this.exhibition.visa_simple_deadline);
    },
    isAverageVisaTimeExpired() {
      return moment() >= moment(this.exhibition.visa_difficult_deadline);
    },
    isVisaDisabled() {
      if (!this.citizenship) return true;
      if (this.isCountryVisaSimple && this.isSimpleVisaTimeExpired) return true;
      return !this.isCountryVisaSimple && this.isAverageVisaTimeExpired;
    },
    isVisaSubmittingDisabled() {
      return (
        (this.isVisa &&
          this.isCountryVisaSimple &&
          this.isSimpleVisaTimeExpired) ||
        (this.isVisa &&
          !this.isCountryVisaSimple &&
          this.isAverageVisaTimeExpired)
      );
    },
    locale() {
      return i18n.locale;
    },
    startDate() {
      return moment(this.exhibition.started_at)
        .locale(this.locale)
        .format("DD MMMM YYYY");
    },
    endDate() {
      return moment(this.exhibition.ended_at)
        .locale(this.locale)
        .format("DD MMMM YYYY");
    },
    isDaysUntilInstallationDeadline() {
      if (!this.passDeadline.startInstallation) return false;
      const now = moment();
      const deadline = moment(this.passDeadline.startInstallation);
      const daysUntilDeadline = deadline.diff(now, "days");
      return daysUntilDeadline >= 5;
    }
  },

  methods: {
    getLocaleValue,
    autoFillCompanyName() {
      const company = this.$store.getters["event/company"];
      if (company)
        this.companyName =
          this.locale === "en" ? company.name_eng : company.name_rus;
    },
    async updateParticipantPass() {
      this.isLoading = true;
      await this.$store.dispatch("passes/fetchParticipantPasses", {
        eventId: this.eventId,
        page: this.currentPage
      });
      this.isLoading = false;
    },
    async beforeOpen() {
      this.autoFillCompanyName();
      await this.updateParticipantPass();
      await this.getCountryList();
    },

    async getCountryList() {
      apiCommon.getCountryList(1).then(({ data }) => {
        this.countryList = data.map(item => {
          return {
            value: item.id,
            title: this.locale === "ru" ? item.name_rus : item.name_eng,
            isVisaSimple: item.is_simple_visa
          };
        });
      });
    },
    async paginate(page) {
      this.isLoadingWithOpacity = true;
      this.currentPage = page;
      await this.$store.dispatch("passes/fetchParticipantPasses", {
        eventId: this.eventId,
        page: this.currentPage
      });
      this.isLoadingWithOpacity = false;
    },
    async sortingPasses() {
      this.isLoadingWithOpacity = true;
      await this.$store.dispatch("passes/fetchParticipantPasses", {
        eventId: this.eventId,
        page: this.currentPage
      });
      this.isLoadingWithOpacity = false;
    },
    async uploadPackage(file) {
      try {
        this.isLoading = true;
        await this.$store.dispatch("passes/uploadParticipantPasses", {
          eventId: this.event.id,
          file
        });
      } catch (error) {
        this.$store.dispatch("common/setErrorMessage", getError(error));
      } finally {
        this.isAddFormShown = false;
        this.isLoading = false;
      }
    },

    submit() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.storeAndClear();
        }
      });
    },

    async storeAndClear() {
      this.isAddFormSubmitting = true;
      const type = this.isVisa ? "visa" : "certificate";
      const pass = this.isVisa
        ? {
            company_name: this.companyName,
            lastname: this.lastName,
            name: this.firstName,
            middlename: this.middleName,
            birthday: moment(this.birthday, "DD.MM.YYYY").format("YYYY-MM-DD"),
            birthday_place: this.birthplace,
            country_id: this.citizenship
          }
        : {
            company_name: this.companyName,
            lastname: this.lastName,
            name: this.firstName,
            middlename: this.middleName
          };
      await this.$store
        .dispatch("passes/addParticipantPass", {
          eventId: this.event.id,
          pass,
          type
        })
        .then(() => {
          this.companyName = "";
          this.lastName = "";
          this.firstName = "";
          this.middleName = "";
          this.birthday = "";
          this.birthplace = "";
          this.isVisa = false;
          this.$validator.reset();
          this.$validator.pause();

          this.isAddFormShown = false;

          this.autoFillCompanyName();
        })
        .catch(error => {
          this.$store.dispatch("common/setErrorMessage", getError(error));
        })
        .finally(() => {
          this.isAddFormSubmitting = false;
        });
    },

    async deletePass(pass) {
      this.isLoadingWithOpacity = true;
      await this.$store.dispatch("passes/removeParticipantPass", {
        eventId: this.eventId,
        passId: pass.id
      });
      this.isLoadingWithOpacity = false;
    },

    async deleteAllPasses() {
      this.isLoading = true;
      await this.$store.dispatch("passes/removeAllParticipantPasses", {
        eventId: this.eventId
      });
      this.isLoading = false;
    },

    async downloadPass(pass, print = false) {
      try {
        this.isLoading = true;
        const response = await apiPersonalPass.downloadPersonalPass(
          this.eventId,
          pass.id
        );
        let blob = new Blob([response], {
          type: "application/pdf"
        });
        const url = window.URL.createObjectURL(blob);
        if (print) {
          printJS(url);
        } else {
          let link = document.createElement("a");
          link.href = url;
          link.download = `Удостоверение участника ${pass.name} ${pass.lastname} (${pass.id}).pdf`;
          link.click();
        }
      } catch (e) {
        await this.$store.dispatch("common/setErrorMessage", getError(e));
      } finally {
        this.isLoading = false;
      }
    },

    printPass(pass) {
      this.downloadPass(pass, true);
    },

    async downloadAllPasses(print = false) {
      try {
        this.isLoading = true;

        const response = await apiPersonalPass.downloadAllPersonalPass(
          this.eventId,
          "certificate"
        );
        if (response) {
          let blob = new Blob([response], {
            type: "application/pdf"
          });
          const url = window.URL.createObjectURL(blob);
          if (print) {
            printJS(url);
          } else {
            let link = document.createElement("a");
            link.href = url;
            link.download = `Пакет удостоверений участников.pdf`;
            link.click();
          }
        }
      } catch (e) {
        await this.$store.dispatch("common/setErrorMessage", getError(e));
      } finally {
        this.isLoading = false;
      }
    },

    printAllPasses() {
      this.downloadAllPasses(true);
    },

    diffInSeconds,

    addNewPass() {
      this.isAddFormShown = true;
      this.$nextTick(() => {
        this.$validator.reset();
        this.$validator.resume();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";

.event-modal-participant-passes {
  &__add {
    background: rgba(#f9f9f9, 0.5);
  }

  &__add + &__users:not(:empty) {
    margin-top: 40px;
    margin-bottom: 50px;
  }
  &__pagination {
    margin: 30px auto 30px;
    @include respond-down(lg) {
      margin: 20px auto 20px;
    }
  }
  &__users {
    padding-left: 80px;
    width: 780px;

    @include respond-down(lg) {
      width: 100%;
      padding: 0 24px 31px 24px;
      box-sizing: border-box;
    }

    @include respond-down(md) {
      width: 100%;
      padding: 0 0 31px 0;
      box-sizing: border-box;
    }
  }
}
.loader-wrap {
  position: relative;
  min-height: 200px;
  max-width: 870px;
}
</style>
